import { useEffect, useRef, useState } from 'react';
import { Button, PageHeader, Textfield } from '@flexo/atoms';
import { FirebaseService, IconWrapper, httpService } from '@flexo/general';
import styles from './mobile-log-in.module.scss';
import { Player } from '@lottiefiles/react-lottie-player';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { ALERT, MODULES, USER } from '@flexo/reducers';
import { useNavigate } from 'react-router-dom';

const ENV = (import.meta as any).env;
const firebaseService = new FirebaseService();

export interface MobileLogInProps {
  bgColor?: string;
  containedLabelTextfield?: boolean;
  customTitle?: string;
  showLogo?: boolean;
  textfieldColor?: 'white' | 'grey' | 'light' | 'primary' | 'secondary' | 'transparent';
  // Add any other props you need
}

export function MobileLogIn({ 
  bgColor, 
  containedLabelTextfield = false,
  customTitle,
  showLogo = true,
  textfieldColor
}: MobileLogInProps) {
  const {
    LogIn__Inputs,
    LogIn__Footer,
    LogIn__Footer__Register,
    LogIn__Footer__Register__Button,
    LogIn__Inputs__PasswordConditions,
  } = styles;

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const [authData, setAuthData] = useState({
    email: '',
    password: '',
    createPassword: '',
  });
  const [logInClicked, setLogInClicked] = useState(false);
  const [logInError, setLogInError] = useState(false);
  const [networkStatus, setNetworkStatus] = useState(false);
  const { t } = useTranslation();

  const [inputType, setInputType] = useState({
    email: 'email',
    password: 'password',
  });
  const [errors, setErrors] = useState({
    email: false,
    password: false,
  });

  const [icon, setIcon] = useState('visibility');

  const {
    title,
    subtitle,
    inputs,
    buttonLabel,
    footer,
    onButtonClick,
    passwordConditions,
  } = t('pages.login.data', { returnObjects: true }) as any;

  const loader = '/loaderLottie.json';

  const handleInputChange = (event: any) => {
    setAuthData({ ...authData, [event.target.name]: event.target.value });
  };

  const signIn = async (event: any) => {
    if (logInClicked) return;
    setLogInClicked(true);
    setLogInError(false);
    try {
      // First, get the user token.
      const response: any = await httpService.post({
        url: `${ENV.VITE_APP_FIREBASE_CUSTOM_TOKEN}`,
        data: {
          email: authData.email,
          password: authData.password,
        },
      });
      // Use the token from the response to sign in.
      const firebaseResult = await firebaseService.signInWithToken(
        response?.data?.token
      );

      dispatch(USER.SetUserAsync(firebaseResult as any) as any);

      navigate(`/${ENV.VITE_APP_FIRST_PAGE}`);
      //selected entity is set in site overview resolver

      return firebaseResult;
    } catch (error: any) {
      if (error.message === 'ERR_NETWORK') {
        // console.error('Network error', error);

        dispatch(
          ALERT.SetAlert({
            payload: {
              value: {
                type: 'BANNER_UNEXPECTED_ERROR',
              } as any,
            },
          } as any)
        );
      } else {
        dispatch(
          ALERT.SetAlert({
            payload: {
              value: {
                type: 'BANNER_WRONG_LOGIN',
              } as any,
            },
          } as any)
        );
      }

      setLogInError(true);

      Sentry.captureEvent({
        // event_id: 'fetchData_no_data',
        message: `Performed request through firebase: error`,
        release: `${ENV.VITE_APP_VERSION}(${ENV.VITE_APP_BUILD})`,
        level: 'warning' as any,
        extra: {
          error,
        },
      });
      setAuthData({ ...authData, password: '' });
      // console.error(error);
    } finally {
      // Reset logInClicked state after attempt.
      setLogInClicked(false);
    }
  };



  const togglePasswordVisibility = (name: string) => {
    setInputType((prevState) => ({
      ...prevState,
      [name]: prevState[name] === 'password' ? 'text' : 'password',
    }));
    setIcon(icon === 'visibility' ? 'hide-visibility' : 'visibility');
  };

  const isEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    return emailRegex.test(email);
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    if (name !== 'email' || !value.length) return;
    if (!isEmail(value)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: true,
      }));
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: false,
      }));
    }
  };

  const forgotPassword = () => {
    dispatch(MODULES.ActivateModuleByName('forgot-password-mobile' as any));
    //TODO change to navigate
    navigate('/forgot-password-mobile');
    //Needs to update module, not working right now
  };
  const register = () => {
    dispatch(MODULES.ActivateModuleByName('register' as any));
    navigate('/register');
    //Needs to update module, not working right now

    //TODO change to navigate
  };



    // Check if user was redirected after password update
    useEffect(() => {
      // Check both localStorage and sessionStorage for reliability
      const passwordUpdatedLocal = localStorage.getItem('passwordUpdated');
      const passwordUpdatedSession = sessionStorage.getItem('passwordUpdated');
      
      if (passwordUpdatedLocal === 'true' || passwordUpdatedSession === 'true') {
        // Remove the flags from both storage locations
        localStorage.removeItem('passwordUpdated');
        sessionStorage.removeItem('passwordUpdated');
        
        // Show password reset success message - ensure we use the correct format
        dispatch(ALERT.ResetAlert());
        
        // Make sure we're using the correct format for alerts
        dispatch(
          ALERT.SetAlert({
            value: {
              type: 'BANNER_PASSWORD_RESET_SUCCESS',
            },
          } as any)
        );
        
        console.log('Password update success banner shown');
      }
    }, []); // Run once on mount
  
    console.log(inputs)
  return (
    <div className={styles.LogIn} >
      <PageHeader title={customTitle || title} subtitle={subtitle} />
      <div className={styles.Login__Content} style={{ backgroundColor: bgColor ? bgColor : '#F7F7F7' }}>
        <div className={LogIn__Inputs}>
          {inputs.map((input: any) => (
            <div key={input.name}>
              <Textfield
              variant={containedLabelTextfield ? "containedLabel" : "default"}
              name={input.name}
                label={input.label}
                type={inputType[input.name]}
                placeholder={input.placeholder}
                error={errors[input.name]}
                value={authData[input.name]}
                onChange={(event) => handleInputChange(event)}
                onPaste={(e) => false}
                onCopy={(e) => false}
                description={input.description}
                errorDescription={
                  errors[input.name] && 'Inserisci una mail valida'
                }
                icon={input.name === 'password' ? icon : ''}
                onIconClick={togglePasswordVisibility}
                onDescriptionClick={() => forgotPassword()}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                onBlur={handleBlur}
                textfieldColor={textfieldColor || 'white'}
              />

         
            </div>
          ))}
        </div>
        <div className={LogIn__Footer}>
          <Button
            onClick={ signIn}
            label={
              logInClicked && !logInError ? (
                <Player autoplay loop src={loader} style={{ height: 20 }} />
              ) : (
                buttonLabel
              )
            }
            variant="primary"
            color="secondary"
            size="large"
            disabled={
              authData.email === '' ||
              authData.password === '' ||
              (logInClicked && !logInError) ||
              networkStatus ||
              !isEmail(authData.email) ||
              errors.email
            }
          />
          
          <span className={`paragraph ${LogIn__Footer__Register}`}>
            {footer.label}
            <button
              className={`paragraph ${LogIn__Footer__Register__Button} `}
              style={{ fontSize: '1em' }}
              onClick={() => register()}
            >
              {footer.buttonLabel}
            </button>
          </span>{' '}
        </div>
        {showLogo && (
          <div className={styles.LogIn__Logo}>
            <IconWrapper iconName="logo" />
          </div>
        )}
      </div>
    </div>
  );
}

export default MobileLogIn;
